import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  EmptyStateDebugInfo,
  EmptyStateFlowStep,
  EmptyStateFixResult,
  EmptyStateMonitorOptions
} from '@/types/fixEmptyStateIssues';

interface FixEmptyStateIssuesState {
  // Debug tracking
  debugEnabled: boolean;
  debugHistory: EmptyStateFlowStep[];
  lastDebugInfo: EmptyStateDebugInfo | null;
  
  // Fix tracking
  lastFixResult: EmptyStateFixResult | null;
  fixInProgress: boolean;
  
  // Monitoring
  monitoringEnabled: boolean;
  monitoringOptions: EmptyStateMonitorOptions;
  consecutiveIssues: number;
  lastMonitorCheck: string | null;
  
  // Statistics
  stats: {
    totalFixes: number;
    successfulFixes: number;
    failedFixes: number;
    detectionIssues: number;
    transitionIssues: number;
  };
  
  // Current state tracking
  currentDetectionState: {
    shouldShowEmpty: boolean;
    actuallyShowingEmpty: boolean;
    lastSyncTime: string | null;
    inconsistencyCount: number;
  };
}

const initialState: FixEmptyStateIssuesState = {
  debugEnabled: false,
  debugHistory: [],
  lastDebugInfo: null,
  
  lastFixResult: null,
  fixInProgress: false,
  
  monitoringEnabled: false,
  monitoringOptions: {
    intervalMs: 2000,
    maxConsecutiveIssues: 3,
    enableAutoFix: true,
    debugMode: false
  },
  consecutiveIssues: 0,
  lastMonitorCheck: null,
  
  stats: {
    totalFixes: 0,
    successfulFixes: 0,
    failedFixes: 0,
    detectionIssues: 0,
    transitionIssues: 0,
  },
  
  currentDetectionState: {
    shouldShowEmpty: false,
    actuallyShowingEmpty: false,
    lastSyncTime: null,
    inconsistencyCount: 0,
  },
};

const fixEmptyStateIssuesSlice = createSlice({
  name: 'fixEmptyStateIssues',
  initialState,
  reducers: {
    // Debug actions
    enableDebug: (state) => {
      state.debugEnabled = true;
    },

    disableDebug: (state) => {
      state.debugEnabled = false;
    },

    addDebugStep: (state, action: PayloadAction<EmptyStateFlowStep>) => {
      state.debugHistory.unshift(action.payload);
      
      // Keep only last 50 steps
      if (state.debugHistory.length > 50) {
        state.debugHistory = state.debugHistory.slice(0, 50);
      }
    },

    clearDebugHistory: (state) => {
      state.debugHistory = [];
    },

    updateLastDebugInfo: (state, action: PayloadAction<EmptyStateDebugInfo>) => {
      state.lastDebugInfo = action.payload;
    },

    // Fix tracking actions
    startFix: (state) => {
      state.fixInProgress = true;
      state.stats.totalFixes++;
    },

    completeFix: (state, action: PayloadAction<EmptyStateFixResult>) => {
      state.fixInProgress = false;
      state.lastFixResult = action.payload;
      
      if (action.payload.success) {
        state.stats.successfulFixes++;
        state.consecutiveIssues = 0;
      } else {
        state.stats.failedFixes++;
        state.consecutiveIssues++;
      }
      
      if (action.payload.detectionFixed) {
        state.stats.detectionIssues++;
      }
      
      if (action.payload.transitionApplied) {
        state.stats.transitionIssues++;
      }
    },

    // Monitoring actions
    enableMonitoring: (state, action: PayloadAction<Partial<EmptyStateMonitorOptions>>) => {
      state.monitoringEnabled = true;
      state.monitoringOptions = { ...state.monitoringOptions, ...action.payload };
    },

    disableMonitoring: (state) => {
      state.monitoringEnabled = false;
      state.consecutiveIssues = 0;
      state.lastMonitorCheck = null;
    },

    recordMonitorCheck: (state, action: PayloadAction<{ issueDetected: boolean }>) => {
      state.lastMonitorCheck = new Date().toISOString();
      
      if (action.payload.issueDetected) {
        state.consecutiveIssues++;
      } else {
        state.consecutiveIssues = 0;
      }
    },

    updateMonitoringOptions: (state, action: PayloadAction<Partial<EmptyStateMonitorOptions>>) => {
      state.monitoringOptions = { ...state.monitoringOptions, ...action.payload };
    },

    // Detection state tracking
    updateDetectionState: (state, action: PayloadAction<{
      shouldShowEmpty: boolean;
      actuallyShowingEmpty: boolean;
    }>) => {
      const wasInconsistent = state.currentDetectionState.shouldShowEmpty !== 
                             state.currentDetectionState.actuallyShowingEmpty;
      
      state.currentDetectionState.shouldShowEmpty = action.payload.shouldShowEmpty;
      state.currentDetectionState.actuallyShowingEmpty = action.payload.actuallyShowingEmpty;
      state.currentDetectionState.lastSyncTime = new Date().toISOString();
      
      const isNowInconsistent = action.payload.shouldShowEmpty !== action.payload.actuallyShowingEmpty;
      
      if (isNowInconsistent && !wasInconsistent) {
        state.currentDetectionState.inconsistencyCount++;
      } else if (!isNowInconsistent) {
        state.currentDetectionState.inconsistencyCount = 0;
      }
    },

    // Statistics actions
    resetStats: (state) => {
      state.stats = {
        totalFixes: 0,
        successfulFixes: 0,
        failedFixes: 0,
        detectionIssues: 0,
        transitionIssues: 0,
      };
      state.consecutiveIssues = 0;
      state.currentDetectionState.inconsistencyCount = 0;
    },

    incrementDetectionIssue: (state) => {
      state.stats.detectionIssues++;
    },

    incrementTransitionIssue: (state) => {
      state.stats.transitionIssues++;
    },

    // Utility actions
    reset: (state) => {
      return { ...initialState };
    },

    updateState: (state, action: PayloadAction<Partial<FixEmptyStateIssuesState>>) => {
      Object.assign(state, action.payload);
    },
  },
});

export const {
  enableDebug,
  disableDebug,
  addDebugStep,
  clearDebugHistory,
  updateLastDebugInfo,
  startFix,
  completeFix,
  enableMonitoring,
  disableMonitoring,
  recordMonitorCheck,
  updateMonitoringOptions,
  updateDetectionState,
  resetStats,
  incrementDetectionIssue,
  incrementTransitionIssue,
  reset,
  updateState,
} = fixEmptyStateIssuesSlice.actions;

export default fixEmptyStateIssuesSlice.reducer;